/**
 * useAufgabenCRUD — Vollständiges CRUD + Echtzeit für Aufgaben
 *
 * Features:
 * - Alle Aufgaben laden + Mitarbeiter-Daten separat matchen (kein FK nötig)
 * - Erstellen, Bearbeiten, Löschen, Status ändern
 * - Checkliste verwalten (Unterpunkte)
 * - Mitarbeiter zuweisen / sich selbst zuweisen
 * - Echtzeit-Updates via Supabase Realtime
 * - Robuste Fehlerbehandlung
 *
 * HINWEIS: Kein Foreign Key Join möglich, da aufgaben.zugewiesen_an
 * und aufgaben.erstellt_von keine FK-Beziehung in Supabase haben.
 * Stattdessen laden wir Mitarbeiter separat und matchen client-seitig.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Aufgabe, ChecklistItem, WiederholungTyp } from '@/lib/database.types';
import { toast } from 'sonner';
import {
  fuehreStellungnahmeRechnungsTriggerAus,
  STELLUNGNAHME_AUFGABE_KATEGORIE,
} from '@/lib/stellungnahme-rechnung';

// Mitarbeiter-Kurzinfo für Anzeige
interface MitarbeiterKurz {
  id: string;
  vorname: string;
  nachname: string;
}

// Erweiterte Aufgabe mit Mitarbeiter-Daten (client-seitig gejoined)
export interface AufgabeMitMitarbeiter extends Aufgabe {
  mitarbeiter: MitarbeiterKurz | null;
  mitarbeiter_liste: MitarbeiterKurz[];
  ersteller: MitarbeiterKurz | null;
}

// Neue Aufgabe erstellen
export interface NeueAufgabe {
  titel: string;
  beschreibung?: string;
  business_unit: string;
  akte_id?: string;
  zugewiesen_an?: string;
  zugewiesen_an_liste?: string[];
  erstellt_von?: string;
  prioritaet?: string;
  kategorie?: string;
  faellig_am?: string;
  checkliste?: ChecklistItem[];
  wiedervorlage_datum?: string;
  wiedervorlage_notiz?: string;
  // Wiederkehrende Aufgaben
  wiederholung_typ?: WiederholungTyp;
  wiederholung_intervall?: number;
  wiederholung_wochentage?: number[];
  wiederholung_ende?: string;
  // KI-Agent Felder
  ist_ki_aufgabe?: boolean;
  ki_status?: string;
  ki_feedback?: string;
}

// Aufgabe bearbeiten
export interface AufgabeUpdate {
  titel?: string;
  beschreibung?: string;
  business_unit?: string;
  akte_id?: string | null;
  zugewiesen_an?: string | null;
  zugewiesen_an_liste?: string[];
  prioritaet?: string;
  kategorie?: string;
  status?: string;
  faellig_am?: string | null;
  erledigt_am?: string | null;
  checkliste?: ChecklistItem[];
  wiedervorlage_datum?: string | null;
  wiedervorlage_notiz?: string | null;
  // Wiederkehrende Aufgaben
  wiederholung_typ?: WiederholungTyp | null;
  wiederholung_intervall?: number | null;
  wiederholung_wochentage?: number[] | null;
  wiederholung_ende?: string | null;
  naechste_faelligkeit?: string | null;
  // KI-Agent Felder
  ist_ki_aufgabe?: boolean;
  ki_status?: string | null;
  ki_feedback?: string | null;
}

/**
 * Berechnet die nächste Fälligkeit basierend auf dem Wiederholungstyp
 */
function berechneNaechsteFaelligkeit(
  aktuellesFaelligAm: string,
  typ: WiederholungTyp,
  intervall?: number,
  wochentage?: number[]
): string {
  const datum = new Date(aktuellesFaelligAm);

  switch (typ) {
    case 'taeglich':
      datum.setDate(datum.getDate() + 1);
      break;
    case 'woechentlich':
      if (wochentage && wochentage.length > 0) {
        // Nächsten passenden Wochentag finden
        const aktuellerTag = datum.getDay();
        const sortiert = [...wochentage].sort((a, b) => a - b);
        const naechsterTag = sortiert.find(t => t > aktuellerTag);
        if (naechsterTag !== undefined) {
          datum.setDate(datum.getDate() + (naechsterTag - aktuellerTag));
        } else {
          // Nächste Woche, erster Tag
          datum.setDate(datum.getDate() + (7 - aktuellerTag + sortiert[0]));
        }
      } else {
        datum.setDate(datum.getDate() + 7);
      }
      break;
    case 'monatlich':
      datum.setMonth(datum.getMonth() + 1);
      break;
    case 'jaehrlich':
      datum.setFullYear(datum.getFullYear() + 1);
      break;
    case 'benutzerdefiniert':
      datum.setDate(datum.getDate() + (intervall || 1));
      break;
  }

  return datum.toISOString();
}

export function useAufgabenCRUD() {
  const [aufgaben, setAufgaben] = useState<AufgabeMitMitarbeiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const mitarbeiterCacheRef = useRef<Map<string, MitarbeiterKurz>>(new Map());
  // IDs mit aktuell laufender Status-Änderung — Doppelklick-/Race-Schutz für
  // den "Erledigt"-Button (verhindert doppeltes Auslösen + doppelte Folgeaufgaben).
  const inFlightStatusRef = useRef<Set<string>>(new Set());
  // Sichtbarer Pending-Status pro Aufgabe für UI-Feedback (Spinner/disabled)
  // während eine Status-Änderung gespeichert wird.
  const [pendingStatusIds, setPendingStatusIds] = useState<Set<string>>(new Set());
  // Ref-Spiegel des aktuellen Aufgaben-Snapshots, damit Callbacks wie
  // updateAufgabe ohne Dep-Churn auf den letzten Stand zugreifen können.
  const aufgabenRef = useRef<AufgabeMitMitarbeiter[]>([]);
  useEffect(() => {
    aufgabenRef.current = aufgaben;
  }, [aufgaben]);

  // ===== MITARBEITER LADEN (einmalig, gecached) =====
  const loadMitarbeiter = useCallback(async () => {
    if (mitarbeiterCacheRef.current.size > 0) return; // Bereits geladen
    try {
      const { data } = await supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname');
      if (data) {
        const cache = new Map<string, MitarbeiterKurz>();
        data.forEach(m => cache.set(m.id, { id: m.id, vorname: m.vorname, nachname: m.nachname }));
        mitarbeiterCacheRef.current = cache;
      }
    } catch {
      // Stille Fehlerbehandlung — Mitarbeiter-Namen sind optional
    }
  }, []);

  // ===== AUFGABEN LADEN =====
  const fetchAufgaben = useCallback(async () => {
    try {
      // Mitarbeiter sicherstellen
      await loadMitarbeiter();

      // Aufgaben ohne Join laden (robust, kein FK nötig)
      const { data, error: fetchError } = await supabase
        .from('aufgaben')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        // Tabelle existiert nicht → leeres Array
        if (fetchError.message?.includes('PGRST') || fetchError.message?.includes('does not exist') || fetchError.code === '42P01') {
          setAufgaben([]);
          setError(null);
          return;
        }
        throw fetchError;
      }

      // Client-seitiger Join: Mitarbeiter-Daten matchen
      const cache = mitarbeiterCacheRef.current;
      const enriched: AufgabeMitMitarbeiter[] = (data || []).map((a: any) => {
        const liste = a.zugewiesen_an_liste || [];

        // Robustes Checkliste-Parsing: String → Array konvertieren falls nötig
        let checkliste = a.checkliste;
        if (typeof checkliste === 'string') {
          try {
            checkliste = JSON.parse(checkliste);
          } catch {
            checkliste = [];
          }
        }
        if (!Array.isArray(checkliste)) {
          checkliste = [];
        }
        // Sicherstellen dass jedes Item die erwartete Struktur hat
        checkliste = checkliste.map((item: any) => ({
          id: item.id || crypto.randomUUID(),
          text: item.text || item.titel || '',
          done: item.done ?? item.erledigt ?? false,
        }));

        return {
          ...a,
          checkliste,
          zugewiesen_an_liste: liste,
          mitarbeiter: a.zugewiesen_an ? (cache.get(a.zugewiesen_an) || null) : null,
          mitarbeiter_liste: liste.map((id: string) => cache.get(id)).filter(Boolean) as MitarbeiterKurz[],
          ersteller: a.erstellt_von ? (cache.get(a.erstellt_von) || null) : null,
        };
      });

      setAufgaben(enriched);
      setError(null);
    } catch (err: any) {
      console.error('Fehler beim Laden der Aufgaben:', err);
      setError(err.message || 'Fehler beim Laden');
      setAufgaben([]);
    } finally {
      setLoading(false);
    }
  }, [loadMitarbeiter]);

  // ===== ECHTZEIT-UPDATES =====
  useEffect(() => {
    fetchAufgaben();

    // Realtime Subscription
    const channel = supabase
      .channel('aufgaben-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'aufgaben' }, () => {
        fetchAufgaben();
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [fetchAufgaben]);

  // ===== ERSTELLEN =====
  const createAufgabe = useCallback(async (data: NeueAufgabe): Promise<Aufgabe | null> => {
    try {
      // Nur Felder senden die in der DB existieren
      // checkliste, kategorie, wiedervorlage_* könnten fehlen
      const insertData: Record<string, any> = {
        titel: data.titel,
        status: 'offen',
        prioritaet: data.prioritaet || 'mittel',
        business_unit: data.business_unit,
      };

      // Optionale Felder nur setzen wenn vorhanden
      if (data.beschreibung) insertData.beschreibung = data.beschreibung;
      if (data.akte_id) insertData.akte_id = data.akte_id;
      if (data.zugewiesen_an) insertData.zugewiesen_an = data.zugewiesen_an;
      if (data.zugewiesen_an_liste && data.zugewiesen_an_liste.length > 0) {
        insertData.zugewiesen_an_liste = data.zugewiesen_an_liste;
        // Sync: erster Eintrag wird auch in zugewiesen_an gesetzt
        if (!data.zugewiesen_an) insertData.zugewiesen_an = data.zugewiesen_an_liste[0];
      }
      if (data.erstellt_von) insertData.erstellt_von = data.erstellt_von;
      if (data.faellig_am) insertData.faellig_am = data.faellig_am;

      // Felder die möglicherweise nicht in der DB existieren — sicher versuchen
      if (data.kategorie) insertData.kategorie = data.kategorie;
      if (data.wiedervorlage_datum) insertData.wiedervorlage_datum = data.wiedervorlage_datum;
      if (data.wiedervorlage_notiz) insertData.wiedervorlage_notiz = data.wiedervorlage_notiz;
      // Wiederkehrende Aufgaben
      // KI-Agent Felder
      if (data.ist_ki_aufgabe) insertData.ist_ki_aufgabe = data.ist_ki_aufgabe;
      if (data.ki_status) insertData.ki_status = data.ki_status;

      if (data.wiederholung_typ) {
        insertData.wiederholung_typ = data.wiederholung_typ;
        if (data.wiederholung_intervall) insertData.wiederholung_intervall = data.wiederholung_intervall;
        if (data.wiederholung_wochentage) insertData.wiederholung_wochentage = data.wiederholung_wochentage;
        if (data.wiederholung_ende) insertData.wiederholung_ende = data.wiederholung_ende;
        // Nächste Fälligkeit berechnen
        if (data.faellig_am) {
          insertData.naechste_faelligkeit = berechneNaechsteFaelligkeit(data.faellig_am, data.wiederholung_typ, data.wiederholung_intervall, data.wiederholung_wochentage);
        }
      }

      const { data: result, error } = await supabase
        .from('aufgaben')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        // Wenn ein Feld nicht existiert, nochmal ohne optionale Felder versuchen
        if (error.message?.includes('column') || error.message?.includes('schema cache')) {
          const minimalData: Record<string, any> = {
            titel: data.titel,
            status: 'offen',
            prioritaet: data.prioritaet || 'mittel',
            business_unit: data.business_unit,
          };
          if (data.beschreibung) minimalData.beschreibung = data.beschreibung;
          if (data.zugewiesen_an) minimalData.zugewiesen_an = data.zugewiesen_an;
          if (data.erstellt_von) minimalData.erstellt_von = data.erstellt_von;
          if (data.faellig_am) minimalData.faellig_am = data.faellig_am;

          const { data: retryResult, error: retryError } = await supabase
            .from('aufgaben')
            .insert(minimalData)
            .select()
            .single();

          if (retryError) throw retryError;
          toast.success('Aufgabe erstellt', { description: data.titel });
          await fetchAufgaben();
          return retryResult as Aufgabe;
        }
        throw error;
      }

      toast.success('Aufgabe erstellt', { description: data.titel });
      await fetchAufgaben();
      return result as Aufgabe;
    } catch (err: any) {
      console.error('Fehler beim Erstellen:', err);
      toast.error('Fehler', { description: err.message || 'Aufgabe konnte nicht erstellt werden' });
      return null;
    }
  }, [fetchAufgaben]);

  // ===== BEARBEITEN =====
  const updateAufgabe = useCallback(async (id: string, data: AufgabeUpdate): Promise<boolean> => {
    const isStatusChange = data.status !== undefined;

    // Doppelklick-/Race-Schutz: Läuft bereits eine Status-Änderung für diese
    // Aufgabe, wird ein zweiter Klick ignoriert (kein doppeltes "Erledigt",
    // keine doppelten Folgeaufgaben).
    if (isStatusChange) {
      if (inFlightStatusRef.current.has(id)) return false;
      inFlightStatusRef.current.add(id);
      setPendingStatusIds(prev => { const next = new Set(prev); next.add(id); return next; });

      // Optimistisches UI-Update: Status sofort spiegeln, damit sich der Klick
      // unmittelbar "erledigt" anfühlt. Bei Fehler revertiert fetchAufgaben.
      const neuerStatus = data.status as string;
      setAufgaben(prev => prev.map(a => a.id === id ? {
        ...a,
        status: neuerStatus,
        erledigt_am: neuerStatus === 'erledigt' ? (a.erledigt_am ?? new Date().toISOString()) : null,
      } : a));
    }

    try {
      // Nur sichere Felder senden (checkliste könnte nicht in DB existieren)
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      // Sichere Felder einzeln setzen
      const safeFields = ['titel', 'beschreibung', 'business_unit', 'akte_id', 'zugewiesen_an', 'zugewiesen_an_liste', 'prioritaet', 'status', 'faellig_am', 'erledigt_am', 'kategorie', 'checkliste', 'wiedervorlage_datum', 'wiedervorlage_notiz', 'wiederholung_typ', 'wiederholung_intervall', 'wiederholung_wochentage', 'wiederholung_ende', 'naechste_faelligkeit', 'ist_ki_aufgabe', 'ki_status', 'ki_feedback'] as const;
      for (const field of safeFields) {
        if (field in data && data[field as keyof AufgabeUpdate] !== undefined) {
          updateData[field] = data[field as keyof AufgabeUpdate];
        }
      }

      // Sync: zugewiesen_an_liste → zugewiesen_an (erster Eintrag)
      if (data.zugewiesen_an_liste) {
        updateData.zugewiesen_an = data.zugewiesen_an_liste.length > 0 ? data.zugewiesen_an_liste[0] : null;
      }

      // Wenn Status auf "erledigt" gesetzt wird, erledigt_am setzen
      if (data.status === 'erledigt' && !data.erledigt_am) {
        updateData.erledigt_am = new Date().toISOString();
      }
      // Wenn Status von "erledigt" weggesetzt wird, erledigt_am löschen
      if (data.status && data.status !== 'erledigt') {
        updateData.erledigt_am = null;
      }

      const { error } = await supabase
        .from('aufgaben')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Issue #242 — Workflow „Nach Stellungnahme → Rechnung":
      // Wenn eine Stellungnahmen-Aufgabe als erledigt (= versendet) markiert
      // wird, automatisch eine Folge-Rechnungsaufgabe anlegen. Fehlschläge
      // des Triggers dürfen den eigentlichen Status-Update nicht kippen.
      if (data.status === 'erledigt') {
        const current = aufgabenRef.current.find(a => a.id === id);
        if (current?.kategorie === STELLUNGNAHME_AUFGABE_KATEGORIE) {
          try {
            const ergebnis = await fuehreStellungnahmeRechnungsTriggerAus({
              stellungnahmeAufgabe: {
                id: current.id,
                business_unit: current.business_unit,
                akte_id: current.akte_id,
                titel: current.titel,
                kategorie: current.kategorie,
              },
              neuerStatus: 'erledigt',
            });
            if (ergebnis) {
              toast.success('Rechnungs-Aufgabe angelegt', { description: ergebnis.titel });
            }
          } catch (triggerErr) {
            console.error('Stellungnahme → Rechnung Trigger fehlgeschlagen:', triggerErr);
          }
        }
      }

      await fetchAufgaben();
      return true;
    } catch (err: any) {
      console.error('Fehler beim Bearbeiten:', err);
      toast.error('Fehler', { description: err.message || 'Aufgabe konnte nicht bearbeitet werden' });
      // Optimistisches Update zurücksetzen (echten DB-Stand laden)
      if (isStatusChange) {
        await fetchAufgaben();
      }
      return false;
    } finally {
      if (isStatusChange) {
        inFlightStatusRef.current.delete(id);
        setPendingStatusIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }
    }
  }, [fetchAufgaben]);

  // ===== STATUS ÄNDERN (Schnellaktion) =====
  const toggleStatus = useCallback(async (id: string, currentStatus: string): Promise<boolean> => {
    const newStatus = currentStatus === 'erledigt' ? 'offen' : 'erledigt';

    // Bei wiederkehrenden Aufgaben: nächste Instanz erstellen statt nur Status ändern
    if (newStatus === 'erledigt') {
      const aufgabe = aufgaben.find(a => a.id === id);
      if (aufgabe?.wiederholung_typ && aufgabe.faellig_am) {
        // Prüfen ob Wiederholung noch aktiv (Ende nicht überschritten)
        const wiederholungAktiv = !aufgabe.wiederholung_ende || new Date(aufgabe.wiederholung_ende) > new Date();

        if (wiederholungAktiv) {
          const naechsteFaelligkeit = berechneNaechsteFaelligkeit(
            aufgabe.faellig_am,
            aufgabe.wiederholung_typ as WiederholungTyp,
            aufgabe.wiederholung_intervall || undefined,
            aufgabe.wiederholung_wochentage || undefined
          );

          // Nächste Fälligkeit in der aktuellen Aufgabe aktualisieren
          const statusOk = await updateAufgabe(id, {
            status: newStatus,
            naechste_faelligkeit: naechsteFaelligkeit
          });

          // Wenn der Status-Update nicht durchging (Doppelklick bereits in
          // Bearbeitung oder Fehler), KEINE doppelte Folgeaufgabe anlegen.
          if (!statusOk) return false;

          // Neue Aufgabe für die nächste Wiederholung erstellen
          await createAufgabe({
            titel: aufgabe.titel,
            beschreibung: aufgabe.beschreibung || undefined,
            business_unit: aufgabe.business_unit,
            zugewiesen_an: aufgabe.zugewiesen_an || undefined,
            zugewiesen_an_liste: aufgabe.zugewiesen_an_liste || undefined,
            erstellt_von: aufgabe.erstellt_von || undefined,
            prioritaet: aufgabe.prioritaet,
            kategorie: aufgabe.kategorie || undefined,
            faellig_am: naechsteFaelligkeit,
            wiederholung_typ: aufgabe.wiederholung_typ as WiederholungTyp,
            wiederholung_intervall: aufgabe.wiederholung_intervall || undefined,
            wiederholung_wochentage: aufgabe.wiederholung_wochentage || undefined,
            wiederholung_ende: aufgabe.wiederholung_ende || undefined,
          });

          toast.success('Erledigt — nächste Wiederholung erstellt', {
            description: `Nächste Fälligkeit: ${new Date(naechsteFaelligkeit).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
          });
          return true;
        }
      }
    }

    return updateAufgabe(id, { status: newStatus });
  }, [updateAufgabe, aufgaben, createAufgabe]);

  // ===== ZUWEISEN =====
  const assignToMe = useCallback(async (aufgabeId: string, mitarbeiterId: string): Promise<boolean> => {
    // Bestehende Liste holen und Mitarbeiter hinzufügen
    const aufgabe = aufgaben.find(a => a.id === aufgabeId);
    const currentList = aufgabe?.zugewiesen_an_liste || [];
    if (!currentList.includes(mitarbeiterId)) {
      const newList = [...currentList, mitarbeiterId];
      const result = await updateAufgabe(aufgabeId, { zugewiesen_an: newList[0], zugewiesen_an_liste: newList });

      // Benachrichtigung erstellen (nur wenn nicht sich selbst zuweist)
      if (result && aufgabe) {
        try {
          await supabase.from('notifications').insert({
            mitarbeiter_id: mitarbeiterId,
            type: 'aufgabe',
            title: 'Neue Aufgabe zugewiesen',
            message: aufgabe.titel,
            link: '/aufgaben',
            is_read: false,
          });
        } catch (e) {
          // Benachrichtigung ist optional, Fehler ignorieren
        }
      }
      return result;
    }
    return true;
  }, [updateAufgabe, aufgaben]);

  const unassign = useCallback(async (aufgabeId: string): Promise<boolean> => {
    return updateAufgabe(aufgabeId, { zugewiesen_an: null, zugewiesen_an_liste: [] });
  }, [updateAufgabe]);

  // Einzelnen Mitarbeiter aus der Liste entfernen
  const removeAssignee = useCallback(async (aufgabeId: string, mitarbeiterId: string): Promise<boolean> => {
    const aufgabe = aufgaben.find(a => a.id === aufgabeId);
    const currentList = aufgabe?.zugewiesen_an_liste || [];
    const newList = currentList.filter(id => id !== mitarbeiterId);
    return updateAufgabe(aufgabeId, {
      zugewiesen_an: newList.length > 0 ? newList[0] : null,
      zugewiesen_an_liste: newList
    });
  }, [updateAufgabe, aufgaben]);

  // ===== CHECKLISTE AKTUALISIEREN =====
  const updateCheckliste = useCallback(async (aufgabeId: string, checkliste: ChecklistItem[]): Promise<boolean> => {
    return updateAufgabe(aufgabeId, { checkliste });
  }, [updateAufgabe]);

  // ===== LÖSCHEN =====
  const deleteAufgabe = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('aufgaben')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Aufgabe gelöscht');
      await fetchAufgaben();
      return true;
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      toast.error('Fehler', { description: err.message || 'Aufgabe konnte nicht gelöscht werden' });
      return false;
    }
  }, [fetchAufgaben]);

  // ===== MITARBEITER-CACHE REFRESH (für Dropdown) =====
  const refreshMitarbeiterCache = useCallback(async () => {
    mitarbeiterCacheRef.current = new Map(); // Cache leeren
    await loadMitarbeiter();
    await fetchAufgaben(); // Aufgaben mit neuen Mitarbeiter-Daten laden
  }, [loadMitarbeiter, fetchAufgaben]);

  return {
    aufgaben,
    loading,
    error,
    refetch: fetchAufgaben,
    createAufgabe,
    updateAufgabe,
    toggleStatus,
    pendingStatusIds,
    assignToMe,
    unassign,
    removeAssignee,
    updateCheckliste,
    deleteAufgabe,
    refreshMitarbeiterCache,
  };
}
